import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Download, Loader2, LogOut, RefreshCw, Trash2, Undo2 } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { KycVerify } from "@/components/KycVerify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "columbus_admin_token";

const inputCls =
  "w-full border border-[#1E4437] bg-[#102920] px-4 py-3.5 text-sm text-[#FBF8F3] placeholder:text-[#B0C2B9]/50 outline-none transition-colors duration-300 focus:border-[#C87D55]";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-CH", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const formatError = (detail, fallback) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(" ");
  return fallback;
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setSubs([]);
  }, []);

  const fetchSubs = useCallback(async () => {
    setLoadingSubs(true);
    try {
      const { data } = await axios.get(`${API}/admin/submissions`, authHeaders());
      setSubs(data.submissions || []);
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally {
      setLoadingSubs(false);
    }
  }, [authHeaders, logout]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, authHeaders());
        if (!cancelled) {
          setUser(data);
          setChecking(false);
        }
      } catch {
        if (!cancelled) {
          logout();
          setChecking(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, authHeaders, logout]);

  useEffect(() => {
    if (user) fetchSubs();
  }, [user, fetchSubs]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
    } catch (e2) {
      setError(formatError(e2.response?.data?.detail, "Connexion impossible. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (sub) => {
    const next = sub.status === "new" ? "contacted" : "new";
    try {
      await axios.patch(`${API}/admin/submissions/${sub.id}`, { status: next }, authHeaders());
      setSubs((prev) => prev.map((s) => (s.id === sub.id ? { ...s, status: next } : s)));
    } catch {
      toast.error("Mise à jour impossible.");
    }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Supprimer la demande de ${sub.name} ?`)) return;
    try {
      await axios.delete(`${API}/admin/submissions/${sub.id}`, authHeaders());
      setSubs((prev) => prev.filter((s) => s.id !== sub.id));
      toast.success("Demande supprimée.");
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Nom", "Email", "Téléphone", "Profil", "Statut", "Date", "Message"],
      ...subs.map((s) => [s.name, s.email, s.phone, s.investor_type, s.status, formatDate(s.created_at), s.message]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `columbus-demandes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A1F18]">
        <Loader2 className="h-6 w-6 animate-spin text-[#C87D55]" data-testid="admin-loading" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-[#0A1F18] px-4 py-16">
        <div className="w-full max-w-md border border-[#1E4437] bg-[#102920] p-10" data-testid="admin-login-card">
          <LogoFull />
          <h1 className="mt-10 font-display text-2xl font-semibold text-[#FBF8F3]">Portail admin</h1>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#B0C2B9]">
            Demandes de consultation · Columbus Management Consulting GmbH
          </p>
          <form onSubmit={login} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#B0C2B9]">Email</label>
              <input
                data-testid="admin-login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="admin@luzerner-realestate.ch"
              />
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#B0C2B9]">
                Mot de passe
              </label>
              <input
                data-testid="admin-login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="border border-[#D8232A]/40 bg-[#D8232A]/10 px-4 py-3 text-sm text-[#F87171]">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit-button"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 bg-[#C87D55] px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#0A1F18] transition-colors duration-300 hover:bg-[#FBF8F3] disabled:opacity-50"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Se connecter
            </button>
          </form>
          <Link
            data-testid="back-to-site-link"
            to="/"
            className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#B0C2B9] transition-colors hover:text-[#C87D55]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Retour au site
          </Link>
        </div>
        <div className="w-full max-w-md">
          <KycVerify />
          <Link
            to="/aml-crypto"
            data-testid="admin-aml-crypto-link"
            className="group mt-6 flex items-center justify-between border border-[#1E4437] bg-[#102920] p-6 transition-colors duration-300 hover:border-[#C87D55]"
          >
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B0C2B9]">
                Vérification d'adresse crypto &amp; dépôt de documents KYC
              </span>
              <p className="mt-1 font-display text-lg font-semibold text-[#FBF8F3]">
                AML Crypto — Partenaire de vérification
              </p>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0 text-[#C87D55] transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    );
  }

  const newCount = subs.filter((s) => s.status === "new").length;

  return (
    <div className="min-h-screen bg-[#0A1F18]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0A1F18]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <LogoFull />
          <div className="flex items-center gap-3">
            <Link
              data-testid="admin-back-to-site-link"
              to="/"
              className="hidden items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B0C2B9] transition-colors hover:text-[#FBF8F3] sm:flex"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B0C2B9] transition-colors hover:border-[#D8232A] hover:text-[#F87171]"
            >
              <LogOut className="h-3.5 w-3.5" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-[#FBF8F3]">
              Demandes de consultation
            </h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#B0C2B9]">
              Connecté : {user.email}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={fetchSubs}
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B0C2B9] transition-colors hover:border-[#C87D55] hover:text-[#C87D55]"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loadingSubs ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <button
              data-testid="admin-export-csv-button"
              onClick={exportCsv}
              disabled={subs.length === 0}
              className="flex items-center gap-2 bg-[#C87D55] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#0A1F18] transition-colors hover:bg-[#FBF8F3] disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-px border border-[#1E4437] bg-[#1E4437]">
          {[
            { label: "Total", value: subs.length, testid: "admin-stat-total" },
            { label: "Nouvelles", value: newCount, testid: "admin-stat-new" },
            { label: "Contactées", value: subs.length - newCount, testid: "admin-stat-contacted" },
          ].map((s) => (
            <div key={s.label} className="bg-[#102920] p-6" data-testid={s.testid}>
              <span className="font-display text-3xl font-bold text-[#FBF8F3]">{s.value}</span>
              <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.22em] text-[#B0C2B9]">
                {s.label}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-8 overflow-x-auto border border-[#1E4437]">
          <table className="w-full min-w-[900px] text-left" data-testid="submissions-table">
            <thead>
              <tr className="border-b border-[#1E4437] bg-[#102920] font-mono text-[10px] uppercase tracking-[0.2em] text-[#B0C2B9]">
                <th className="px-5 py-4 font-medium">Contact</th>
                <th className="px-5 py-4 font-medium">Profil</th>
                <th className="px-5 py-4 font-medium">Message</th>
                <th className="px-5 py-4 font-medium">Date</th>
                <th className="px-5 py-4 font-medium">Statut</th>
                <th className="px-5 py-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-14 text-center text-sm text-[#B0C2B9]" data-testid="submissions-empty">
                    Aucune demande pour le moment.
                  </td>
                </tr>
              )}
              {subs.map((sub) => (
                <tr
                  key={sub.id}
                  data-testid={`submission-row-${sub.id}`}
                  className="border-b border-[#1E4437]/60 bg-[#0A1F18] align-top transition-colors last:border-0 hover:bg-[#102920]"
                >
                  <td className="px-5 py-4">
                    <span className="block text-sm font-medium text-[#FBF8F3]">{sub.name}</span>
                    <span className="mt-1 block text-xs text-[#B0C2B9]">{sub.email}</span>
                    {sub.phone && <span className="mt-0.5 block font-mono text-xs text-[#B0C2B9]">{sub.phone}</span>}
                  </td>
                  <td className="px-5 py-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#C87D55]">
                      {sub.investor_type || "—"}
                    </span>
                  </td>
                  <td className="max-w-xs px-5 py-4">
                    <p className="line-clamp-3 text-sm leading-relaxed text-[#B0C2B9]" title={sub.message}>
                      {sub.message}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 font-mono text-xs text-[#B0C2B9]">
                    {formatDate(sub.created_at)}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      data-testid={`submission-status-${sub.id}`}
                      className={`inline-block px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
                        sub.status === "new"
                          ? "border border-[#C87D55]/40 bg-[#C87D55]/10 text-[#C87D55]"
                          : "border border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                      }`}
                    >
                      {sub.status === "new" ? "Nouveau" : "Contacté"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2">
                      <button
                        data-testid={`submission-status-toggle-${sub.id}`}
                        onClick={() => toggleStatus(sub)}
                        title={sub.status === "new" ? "Marquer contacté" : "Remettre en nouveau"}
                        className="flex h-8 w-8 items-center justify-center border border-white/15 text-[#B0C2B9] transition-colors hover:border-[#C87D55] hover:text-[#C87D55]"
                      >
                        {sub.status === "new" ? <Check className="h-4 w-4" /> : <Undo2 className="h-4 w-4" />}
                      </button>
                      <button
                        data-testid={`submission-delete-${sub.id}`}
                        onClick={() => remove(sub)}
                        title="Supprimer"
                        className="flex h-8 w-8 items-center justify-center border border-white/15 text-[#B0C2B9] transition-colors hover:border-[#D8232A] hover:text-[#F87171]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
